import { Box } from "@mui/material";
import type { TFunction } from "i18next";
import { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { HighlightedText } from "../../../../../components/UIToolkit/molecules/HighlightedText";
import { FONT_SIZE } from "../../../../../theme/tokens";
import type { AirtableFacilityMatch } from "./airtableFacilityFilter";
import type { AirtableFacilityOption } from "./airtableImportTypes";

/** "Kick-off Aug 26, 2026", or `null` when the facility has no kick-off date. */
function kickoffLabel(option: AirtableFacilityOption, t: TFunction): string | null {
	if (option.kickoffDate === null) return null;
	return t("department.config.airtable.search.kickoffDate", { date: DateTime.fromISO(option.kickoffDate).toLocaleString(DateTime.DATE_MED) });
}

/**
 * Whether a record needs its own title to be told apart: it shares its facility with another record, or
 * links no facility at all, which leaves the title as its only name.
 */
export function needsRecordTitle(option: AirtableFacilityOption, ambiguousNames: ReadonlySet<string>): boolean {
	return option.facilityName === null || ambiguousNames.has(option.facilityName);
}

/** The record as one flat line — the row's accessible name and the "Selected:" confirmation. */
export function facilityLabel(option: AirtableFacilityOption, ambiguousNames: ReadonlySet<string>, t: TFunction): string {
	const name = option.facilityName ?? t("department.config.airtable.search.noFacility");
	const title = needsRecordTitle(option, ambiguousNames) ? option.kickoffRecord : null;
	return [name, option.network, kickoffLabel(option, t), title].filter((part) => part !== null).join(" · ");
}

type Props = {
	match: AirtableFacilityMatch;
	ambiguousNames: ReadonlySet<string>;
};

/**
 * One record in the picker: the facility name, then its network and kick-off date. The Airtable record
 * id is never shown — it identifies nothing to a person. The record's own free-text title appears only
 * when the facility name alone would leave two rows looking the same.
 */
export const AirtableFacilityRow: FC<Props> = ({ match, ambiguousNames }) => {
	const { t } = useTranslation();
	const { option, nameSegments, networkSegments, kickoffSegments } = match;
	const kickoff = kickoffLabel(option, t);
	const showTitle = needsRecordTitle(option, ambiguousNames) && kickoffSegments !== null;

	return (
		<Box component="span" sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
			<Box component="span" sx={{ fontSize: FONT_SIZE.EMPHASIS, fontWeight: 600, lineHeight: 1.35 }}>
				{nameSegments ? (
					<HighlightedText segments={nameSegments} />
				) : (
					<Box component="span" sx={{ color: "text.secondary", fontStyle: "italic" }}>
						{t("department.config.airtable.search.noFacility")}
					</Box>
				)}
			</Box>
			{(networkSegments || kickoff) && (
				<Box component="span" sx={{ fontSize: FONT_SIZE.SECONDARY, color: "text.secondary", lineHeight: 1.35 }}>
					{networkSegments && <HighlightedText segments={networkSegments} />}
					{networkSegments && kickoff && " · "}
					{kickoff}
				</Box>
			)}
			{showTitle && kickoffSegments && (
				<Box component="span" sx={{ fontSize: FONT_SIZE.LABEL_XS, color: "text.secondary", lineHeight: 1.35 }}>
					<HighlightedText segments={kickoffSegments} />
				</Box>
			)}
		</Box>
	);
};
